// /admin/quality-profiles — two-tab quality profile browser (T164).
//
// Tab "Built-in": read-only system profiles (Orbital Way + Orbital Strict),
//   grouped by language with the pair displayed side-by-side. Each card has a
//   "Clone to customize" button that opens a clone dialog; after the clone
//   the user is taken directly to the profile editor (T120).
//
// Tab "Custom": user-managed profiles. Same create / clone / delete behaviour
//   as T119, plus post-clone navigation to the editor.

import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Check,
  ChevronRight,
  Copy,
  Layers,
  Loader2,
  Lock,
  Plus,
  Settings,
  Star,
  Trash2,
} from 'lucide-react';
import {
  ApiError,
  QualityProfile,
  QualityProfileCreate,
  qualityProfilesApi,
  fetcher,
} from '../lib/api';
import { PageHeader } from '../components/ui';

const PROFILES_KEY = '/api/v1/admin/quality-profiles';

type TabId = 'builtin' | 'custom';

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminQualityProfiles() {
  const { data: profiles, error, isLoading, mutate } =
    useSWR<QualityProfile[]>(PROFILES_KEY, fetcher, { revalidateOnFocus: false });

  const [tab, setTab] = useState<TabId>('builtin');
  const [showCreate, setShowCreate] = useState(false);
  const [cloneSrc, setCloneSrc] = useState<QualityProfile | null>(null);
  const navigate = useNavigate();

  const builtinProfiles = (profiles ?? []).filter((p) => p.is_system);
  const customProfiles = (profiles ?? []).filter((p) => !p.is_system);

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Layers />}
        title="Quality Profiles"
        description={
          <>
            Built-in profiles are read-only. Clone one to create a customisable copy,
            then tune rule activation in the profile editor.
          </>
        }
        actions={
          <>
            <Link
              to="/admin/settings"
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Settings className="h-3.5 w-3.5" aria-hidden="true" />
              Settings
            </Link>
            {tab === 'custom' ? (
              <button
                onClick={() => setShowCreate(true)}
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                  text-xs font-semibold uppercase tracking-[0.08em]
                  bg-accent text-accent-fg hover:opacity-90
                  border border-accent/40 transition-opacity
                "
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                New profile
              </button>
            ) : null}
          </>
        }
      />

      {/* Tab bar */}
      <div className="flex gap-0 border-b border-border mb-6" role="tablist">
        <TabButton
          id="builtin"
          label="Built-in"
          count={builtinProfiles.length}
          active={tab === 'builtin'}
          onClick={() => setTab('builtin')}
        />
        <TabButton
          id="custom"
          label="Custom"
          count={customProfiles.length}
          active={tab === 'custom'}
          onClick={() => setTab('custom')}
        />
      </div>

      {(() => {
        if (isLoading) {
          return <LoadingCard />;
        } else if (error) {
          return <ErrorBanner error={error} />;
        } else if (tab === 'builtin') {
          return <BuiltinTab profiles={builtinProfiles} onClone={(p) => setCloneSrc(p)} />;
        } else {
          return (
            <CustomTab
              profiles={customProfiles}
              onCreate={() => setShowCreate(true)}
              onClone={(p) => setCloneSrc(p)}
              onDelete={async (p) => {
                if (!confirm(`Delete profile "${p.name}"? This cannot be undone.`)) return;
                await qualityProfilesApi.delete(p.id);
                mutate();
              }}
            />
          );
        }
      })()}

      {showCreate ? (
        <CreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); mutate(); }}
        />
      ) : null}

      {cloneSrc ? (
        <CloneDialog
          src={cloneSrc}
          onClose={() => setCloneSrc(null)}
          onCloned={(newId) => {
            setCloneSrc(null);
            mutate();
            navigate(`/admin/quality-profiles/${encodeURIComponent(newId)}`);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

function TabButton({
  id,
  label,
  count,
  active,
  onClick,
}: {
  readonly id: string;
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      role="tab"
      aria-selected={active}
      id={`tab-${id}`}
      onClick={onClick}
      className={`
        px-5 py-2.5 text-sm font-medium border-b-2 transition-colors
        ${active
          ? 'border-accent text-accent -mb-px'
          : 'border-transparent text-fg-muted hover:text-fg hover:border-border'}
      `}
    >
      {label}
      <span
        className={`
          ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium
          ${active ? 'bg-accent/15 text-accent' : 'bg-bg-subtle text-fg-subtle'}
        `}
      >
        {count}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Built-in tab — read-only system profiles, Way + Strict side-by-side
// ---------------------------------------------------------------------------

interface BuiltinTabProps {
  readonly profiles: QualityProfile[];
  readonly onClone: (p: QualityProfile) => void;
}

function BuiltinTab({ profiles, onClone }: BuiltinTabProps) {
  const byLanguage = groupByLanguage(profiles);

  if (byLanguage.length === 0) {
    return (
      <div className="orb-card p-10 flex flex-col items-center justify-center gap-3 text-center">
        <Lock className="h-8 w-8 text-fg-subtle" aria-hidden="true" />
        <p className="text-sm text-fg-muted">No built-in profiles are seeded yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {byLanguage.map(([lang, group]) => (
        <BuiltinLanguageSection key={lang} language={lang} profiles={group} onClone={onClone} />
      ))}
    </div>
  );
}

function BuiltinLanguageSection({
  language,
  profiles,
  onClone,
}: {
  readonly language: string;
  readonly profiles: QualityProfile[];
  readonly onClone: (p: QualityProfile) => void;
}) {
  // Way before Strict; other tiers alphabetically after.
  const sorted = [...profiles].sort((a, b) => {
    const order: Record<string, number> = { way: 0, strict: 1 };
    return (order[a.tier] ?? 2) - (order[b.tier] ?? 2);
  });

  return (
    <section className="orb-card overflow-hidden">
      <header className="px-5 py-3 border-b border-border bg-bg-inset flex items-center gap-2">
        <span className="text-xs font-mono font-medium text-accent">{language}</span>
        <span className="text-xs text-fg-subtle">
          {profiles.length} profile{profiles.length === 1 ? '' : 's'}
        </span>
      </header>
      <div className={`grid gap-px bg-border ${sorted.length > 1 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        {sorted.map((p) => (
          <BuiltinProfileCard key={p.id} profile={p} onClone={onClone} />
        ))}
      </div>
    </section>
  );
}

const TIER_META: Record<string, { label: string; chipCls: string }> = {
  way: {
    label: 'Orbital Way',
    chipCls: 'bg-accent/10 text-accent border-accent/20',
  },
  strict: {
    label: 'Orbital Strict',
    chipCls: 'bg-warning/10 text-warning border-warning/20',
  },
};

function BuiltinProfileCard({
  profile,
  onClone,
}: {
  readonly profile: QualityProfile;
  readonly onClone: (p: QualityProfile) => void;
}) {
  const tierMeta = TIER_META[profile.tier];

  return (
    <div className="bg-bg p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          {tierMeta ? (
            <span
              className={`
                self-start inline-flex items-center gap-1 px-2 py-0.5
                rounded-full text-[10px] font-semibold border ${tierMeta.chipCls}
              `}
            >
              {tierMeta.label}
            </span>
          ) : null}
          <p className="text-sm font-medium text-fg">{profile.name}</p>
        </div>
        <span
          className="
            inline-flex items-center gap-1 px-1.5 py-0.5 rounded
            text-[10px] font-medium bg-bg-subtle text-fg-subtle border border-border
            shrink-0
          "
        >
          <Lock className="h-3 w-3" aria-hidden="true" />
          system
        </span>
      </div>

      <p className="text-xs text-fg-muted">
        {profile.rule_count} rule{profile.rule_count === 1 ? '' : 's'}
        {profile.is_default ? (
          <span className="ml-2 inline-flex items-center gap-1 text-warning">
            <Star className="h-3 w-3" aria-hidden="true" />
            default
          </span>
        ) : null}
      </p>

      <button
        onClick={() => onClone(profile)}
        className="
          self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
          text-xs font-semibold uppercase tracking-[0.08em]
          bg-accent text-accent-fg hover:opacity-90
          border border-accent/40 transition-opacity
        "
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        Clone to customize
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom tab — user-managed profiles (create / clone / delete)
// ---------------------------------------------------------------------------

interface CustomTabProps {
  readonly profiles: QualityProfile[];
  readonly onCreate: () => void;
  readonly onClone: (p: QualityProfile) => void;
  readonly onDelete: (p: QualityProfile) => void;
}

function CustomTab({ profiles, onCreate, onClone, onDelete }: CustomTabProps) {
  const byLanguage = groupByLanguage(profiles);

  if (byLanguage.length === 0) {
    return (
      <div className="orb-card p-12 flex flex-col items-center justify-center gap-4 text-center">
        <Layers className="h-10 w-10 text-fg-subtle" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-fg">No custom profiles yet</p>
          <p className="text-xs text-fg-muted mt-1">
            Clone a built-in profile or create a new one from scratch to get started.
          </p>
        </div>
        <button onClick={onCreate} className={primaryBtnCls}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          New profile
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {byLanguage.map(([lang, group]) => (
        <LanguageGroup
          key={lang}
          language={lang}
          profiles={group}
          onClone={onClone}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Language group (custom profiles)
// ---------------------------------------------------------------------------

interface LanguageGroupProps {
  readonly language: string;
  readonly profiles: QualityProfile[];
  readonly onClone: (p: QualityProfile) => void;
  readonly onDelete: (p: QualityProfile) => void;
}

function LanguageGroup({ language, profiles, onClone, onDelete }: LanguageGroupProps) {
  return (
    <section className="orb-card overflow-hidden">
      <header className="px-5 py-3 border-b border-border bg-bg-inset flex items-center gap-2">
        <span className="text-xs font-mono font-medium text-accent">{language}</span>
        <span className="text-xs text-fg-subtle">
          ({profiles.length} profile{profiles.length === 1 ? '' : 's'})
        </span>
      </header>
      <ul className="divide-y divide-border">
        {profiles.map((p) => (
          <ProfileRow key={p.id} profile={p} onClone={onClone} onDelete={onDelete} />
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Profile row (custom)
// ---------------------------------------------------------------------------

interface ProfileRowProps {
  readonly profile: QualityProfile;
  readonly onClone: (p: QualityProfile) => void;
  readonly onDelete: (p: QualityProfile) => void | Promise<void>;
}

function ProfileRow({ profile, onClone, onDelete }: ProfileRowProps) {
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(profile);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <li className="flex items-center justify-between px-5 py-3 hover:bg-bg-inset/50 transition-colors group">
      {/* Navigate button — covers the name/info area */}
      <button
        type="button"
        className="flex items-center gap-3 flex-1 min-w-0 text-left"
        onClick={() => navigate(`/admin/quality-profiles/${encodeURIComponent(profile.id)}`)}
      >
        {profile.is_default ? (
          <Star className="h-3.5 w-3.5 text-warning shrink-0" aria-label="Default profile" />
        ) : (
          <div className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg truncate">{profile.name}</p>
          <p className="text-xs text-fg-subtle">
            {profile.rule_count} rule{profile.rule_count === 1 ? '' : 's'}
            {profile.is_default ? <span className="ml-2 text-warning">default</span> : null}
          </p>
        </div>
      </button>
      {/* Action buttons — siblings of the navigate button, not nested */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <ActionButton
          onClick={() => onClone(profile)}
          title="Clone profile"
          icon={<Copy className="h-3.5 w-3.5" />}
        />
        <ActionButton
          onClick={() => void handleDelete()}
          title="Delete profile"
          icon={deleting
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Trash2 className="h-3.5 w-3.5 text-danger" />
          }
          disabled={deleting}
        />
        <ChevronRight className="h-3.5 w-3.5 text-fg-subtle ml-1" aria-hidden="true" />
      </div>
    </li>
  );
}

function ActionButton({
  onClick,
  title,
  icon,
  disabled,
}: {
  readonly onClick: () => void;
  readonly title: string;
  readonly icon: React.ReactNode;
  readonly disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="
        p-1.5 rounded hover:bg-bg-subtle transition-colors text-fg-muted
        hover:text-fg disabled:opacity-40 disabled:cursor-not-allowed
      "
    >
      {icon}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------

const LANGUAGE_OPTIONS = [
  'go', 'js', 'ts', 'py', 'java', 'cs', 'yaml', 'tf', 'dockerfile', 'sh', '*',
];

interface CreateDialogProps {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function CreateDialog({ onClose, onCreated }: CreateDialogProps) {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('go');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    const body: QualityProfileCreate = { name: name.trim(), language, is_default: isDefault };
    try {
      await qualityProfilesApi.create(body);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <Backdrop onClose={onClose}>
      <Dialog title="New quality profile" onClose={onClose}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogField label="Profile name" htmlFor="qp-name">
            <input
              id="qp-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="My custom profile"
              autoFocus
              required
            />
          </DialogField>

          <DialogField label="Language" htmlFor="qp-lang">
            <select
              id="qp-lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`${inputCls} pr-8`}
            >
              {LANGUAGE_OPTIONS.map((l) => (
                <option key={l} value={l}>{l === '*' ? '* (all languages)' : l}</option>
              ))}
            </select>
          </DialogField>

          <label className="flex items-center gap-2 text-xs text-fg cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="accent-accent"
            />
            {'Set as default profile for this language'}
          </label>

          {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={cancelBtnCls}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtnCls}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
              {saving ? 'Creating…' : 'Create profile'}
            </button>
          </div>
        </form>
      </Dialog>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------
// Clone dialog — navigates to editor on success
// ---------------------------------------------------------------------------

interface CloneDialogProps {
  readonly src: QualityProfile;
  readonly onClose: () => void;
  /** Called with the new profile's ID so the caller can navigate to the editor. */
  readonly onCloned: (newId: string) => void;
}

function CloneDialog({ src, onClose, onCloned }: CloneDialogProps) {
  const [name, setName] = useState(`${src.name} — copy`);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const cloned = await qualityProfilesApi.clone(src.id, name.trim());
      setSaved(true);
      setTimeout(() => onCloned(cloned.id), 500);
    } catch (e) {
      setErr(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <Backdrop onClose={onClose}>
      <Dialog title={`Clone "${src.name}"`} onClose={onClose}>
        <p className="text-xs text-fg-muted mb-4">
          Creates a new <span className="font-mono text-accent">{src.language}</span> profile
          with all {src.rule_count} rules copied from the source. You will be taken
          to the profile editor to tune rule activation.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogField label="New profile name" htmlFor="clone-name">
            <input
              id="clone-name"
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              autoFocus
              required
            />
          </DialogField>

          {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={cancelBtnCls}>Cancel</button>
            <button type="submit" disabled={saving || saved} className={primaryBtnCls}>
              {(() => {
                if (saved) {
                  return <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Cloned!</>;
                } else if (saving) {
                  return <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Cloning…</>;
                } else {
                  return <>Clone profile</>;
                }
              })()}
            </button>
          </div>
        </form>
      </Dialog>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------
// Shared dialog scaffolding
// ---------------------------------------------------------------------------

function Backdrop({ children, onClose }: { readonly children: React.ReactNode; readonly onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop:bg-black/60 p-0 m-0 w-screen h-screen max-w-none max-h-none border-0"
    >
      {children}
    </dialog>
  );
}

function Dialog({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="orb-card w-full max-w-md mx-4 p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-lg font-semibold text-fg">{title}</h2>
        <button
          onClick={onClose}
          className="text-fg-muted hover:text-fg transition-colors text-xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

function DialogField({
  label,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly htmlFor: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="orb-section-title text-[11px]">{label}</label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function LoadingCard() {
  return (
    <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading profiles…</span>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  const msg = formatApiError(error);
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can manage quality profiles.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByLanguage(profiles: QualityProfile[]): [string, QualityProfile[]][] {
  const map = new Map<string, QualityProfile[]>();
  for (const p of profiles) {
    const lang = p.language || '*';
    if (!map.has(lang)) map.set(lang, []);
    map.get(lang)!.push(p);
  }
  return [...map.entries()].sort(([a], [b]) => {
    if (a === '*') return 1;
    if (b === '*') return -1;
    return a.localeCompare(b);
  });
}

function formatApiError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.code}: ${e.message}`;
  } else if (e instanceof Error) {
    return e.message;
  } else {
    return 'request failed';
  }
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();

const cancelBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  border border-border text-fg-muted hover:text-fg hover:border-fg-muted
  transition-colors
`.trim();
